import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, CalendarDays, Users } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useCreateTour } from '@/hooks/useActivities'
import { useTourMembers } from '@/hooks/useTourCrew'
import { useOrg } from '@/context/OrgContext'
import type { Tour } from '@/types'
import { AssignFromOrgPicker } from '@/pages/manager/CrewView'

// Single dialog drives both flows:
//   • mode="create" → just the new-tour form. On submit the body flips to
//     "edit" of the just-created tour so the manager can immediately assign
//     crew without a separate trip back to the grid.
//   • mode="edit"  → tour name + dates editable, crew picker visible.
//
// Crew picker writes directly to DB via AssignFromOrgPicker; no need to lift
// state. We only manage name/dates here.
//
// The body is keyed by `open + tour?.id` so each new opening remounts with a
// clean slate — no useEffect-driven state reset.

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  tour: Tour | null  // null when opening in create mode
}

export function TourEditDialog({ open, onOpenChange, tour }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md gap-0 p-0 overflow-hidden">
        {open && (
          <TourFormBody
            key={tour?.id ?? 'create-new'}
            initialTour={tour}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function TourFormBody({ initialTour, onClose }: { initialTour: Tour | null; onClose: () => void }) {
  const { activeOrg } = useOrg()
  const queryClient = useQueryClient()
  const createTour = useCreateTour()

  // After a successful create, the body flips to editing the just-made tour
  // (so the crew picker appears) without unmounting.
  const [draftTour, setDraftTour] = useState<Tour | null>(initialTour)
  const mode: 'create' | 'edit' = draftTour ? 'edit' : 'create'

  const [nombre, setNombre] = useState(initialTour?.nombre ?? '')
  const [fechaInicio, setFechaInicio] = useState(initialTour?.fecha_inicio ?? '')
  const [fechaFin, setFechaFin] = useState(initialTour?.fecha_fin ?? '')
  const [saving, setSaving] = useState(false)

  function invalidateTours() {
    queryClient.invalidateQueries({ queryKey: ['all-tours'] })
    queryClient.invalidateQueries({ queryKey: ['available-tours'] })
    queryClient.invalidateQueries({ queryKey: ['org-tour-stats'] })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim() || !fechaInicio || !fechaFin || !activeOrg) return

    if (mode === 'create') {
      try {
        const created = await createTour.mutateAsync({
          org_id: activeOrg.id,
          nombre: nombre.trim(),
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        })
        await queryClient.refetchQueries({ queryKey: ['all-tours', activeOrg.id] })
        toast.success('Tour created — now assign crew')
        setDraftTour(created as Tour)
      } catch {
        toast.error('Failed to create tour')
      }
      return
    }

    if (!draftTour) return
    setSaving(true)
    const { error } = await supabase
      .from('tours')
      .update({
        nombre: nombre.trim(),
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      })
      .eq('id', draftTour.id)
    setSaving(false)
    if (error) {
      toast.error('Failed to update tour')
      return
    }
    toast.success('Tour updated')
    invalidateTours()
    onClose()
  }

  const isLoading = createTour.isPending || saving
  const title = mode === 'create' ? 'New tour' : 'Edit tour'

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col">
      <div className="px-5 pt-5 pb-3">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="mt-1">
          {mode === 'create'
            ? 'Set a name and dates. Crew can be added in the next step.'
            : 'Update the tour name, dates, and crew.'}
        </DialogDescription>
      </div>

      <div className="px-5 pb-5 space-y-4">
        <Input
          placeholder="Tour name"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className="h-11 text-base font-medium border-none bg-muted/60 rounded-lg px-3.5 placeholder:text-muted-foreground/60 focus-visible:ring-1"
          required
          autoFocus
        />

        <div className="rounded-lg border border-border bg-card-2 overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">
              Dates
            </span>
          </div>
          <DateRow label="Start" value={fechaInicio} onChange={setFechaInicio} />
          <DateRow label="End" value={fechaFin} onChange={setFechaFin} />
        </div>

        {mode === 'edit' && draftTour && activeOrg && (
          <CrewSection tourId={draftTour.id} orgId={activeOrg.id} />
        )}
      </div>

      <div className="flex gap-2 border-t border-border bg-muted/40 px-5 py-3.5">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1 h-10"
        >
          {mode === 'edit' ? 'Done' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !nombre.trim() || !fechaInicio || !fechaFin}
          className="flex-1 h-10 font-medium"
        >
          {isLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

function DateRow({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2 border-t border-border first-of-type:border-t-0">
      <span className="text-sm text-foreground shrink-0 min-w-[40px]">{label}</span>
      <div className="flex-1 flex justify-end">
        <Input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 text-sm border-none bg-transparent p-0 text-right focus-visible:ring-0"
          required
        />
      </div>
    </div>
  )
}

function CrewSection({ tourId, orgId }: { tourId: string; orgId: string }) {
  const { data: crew = [] } = useTourMembers(tourId)
  const tourMemberIds = useMemo(() => new Set(crew.map(m => m.user_id)), [crew])

  return (
    <div className="rounded-lg border border-border bg-card-2 overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
        <Users className="w-4 h-4 text-muted-foreground" />
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">
          Crew
        </span>
        <span className="text-[11px] text-muted-foreground/70">· {crew.length}</span>
      </div>
      <div className="px-4 pb-4 pt-1">
        <AssignFromOrgPicker orgId={orgId} tourId={tourId} tourMemberIds={tourMemberIds} />
      </div>
    </div>
  )
}
